import React, {useEffect, useRef} from 'react';
import Page from '../../../components/layout/Page';
import {type TPandasTable} from '../../../models/TPandasTable';
import SvCalculatorServices from '../../../services/SvCalculatorServices';
import {getFactsheetId, updateStateAttribute, useStateExtended} from '../../../helpers/helper';
import CPageModule from '../../../components/CPageModule';
import {useAppSelector} from '../../../hooks/hooks';
import type {IOptionsCalculatorConfig} from '../../../models/IOptionsCalculatorConfig';
import {type IResponseTableOrPlot} from '../../../models/IResponseTableOrPlot';
import CLocalizedText from '../../../components/CLocalizedText';
import {type IResponse} from '../../../models/IResponse';
import CLocalizedTextString from '../../../components/CLocalizedTextString';
import {EOptionsCalculatorParameters} from '../../../enums/EOptionsCalculatorParameters';
import CCalculatorParameters from './CCalculatorParameters';
import {type PlotlyResponse} from '../../../models/PlotlyModel';
import CCalculatorTable from './CCalculatorTable';
import CCalculator2dPlotManager from './CCalculator2dPlotManager';
import CCalculatorSimulation from './CCalculatorSimulation';
import {ELocalStorageItems} from '../../../enums/ELocalStorageItems';
import {type I2DPlotConfig, type I2DPlotState} from '../../../models/ICalculatorPlot';
import {type I3DPlotState} from '../../../models/calculator/I3DPlotStateModel';
import {type I3DPlotConfig} from '../../../models/calculator/I3DPlotConfigModel';
import {useDebouncedCallback} from '../../../hooks/useDebouncedCallback';
import {EFactsheetClasses} from '../../../enums/EFactsheetClasses';
import {useKeycloak} from '@react-keycloak/web';
import CCalculator3dPlotManager from './CCalculator3dPlotManager';
import {SvUsageTrackingServices} from '../../../services/SvUsageTrackingServices';
import {useUsageTrackingInfo} from '../../../hooks/useUsageTrackingInfo';
import {type IUsageTrackingInfo} from '../../../models/usage-tracking/IUsageTracking';
import {EAppType} from '../../../enums/AppType';

export interface PCalculatorIndexState {
  isPageInitialized: boolean;
  showParameterDescriptionModal: boolean;
  optionsConfigLoaded: IOptionsCalculatorConfig;
  optionsConfigInput: IOptionsCalculatorConfig;
  optionsTable: IResponseTableOrPlot<TPandasTable>;
  optionsTableDerivatives: IResponse<{ options: Array<{label: string, value: string }> }>;
  plot2dPlots: I2DPlotState[];
  plot3dPlots:I3DPlotState[]
  plotSimulation: IResponseTableOrPlot<{
    call: PlotlyResponse, put: PlotlyResponse, call_payoff: number, put_payoff: number
  }>;
  targetScrollProgress: number;
}

export default function PCalculatorIndex() {
  const optionsConfigDefault: IOptionsCalculatorConfig = {
    S: 100,
    K: 90,
    v: 0.35,
    T: 0.5,
    r: 0.05,
    q: 0.02,
    derivatives: ['Price', 'Delta', 'Gamma', 'Vega', 'Theta', 'Rho'],
    plot3dConfigs:[{
      id: 'plot3d-1',
      plot3dX: EOptionsCalculatorParameters.S,
      plot3dY: EOptionsCalculatorParameters.v,
      plot3dZ:'Delta'
    }],
    plot2dConfigs: [{
      id: 'plot2d-1', plotX: EOptionsCalculatorParameters.S, plotY: ['Price']
    }]
  };

  const stickyHeaderRef = useRef<HTMLDivElement | null>(null);

  const { keycloak } = useKeycloak();

  const factsheetId = getFactsheetId(EFactsheetClasses.US_AMERICAN_STOCKS);

  const usageTrackingParams: IUsageTrackingInfo = useUsageTrackingInfo(EAppType.Calculator);

  const parameterOptions = Object.values(EOptionsCalculatorParameters).map((e) => {
    return { label: CLocalizedTextString(`topics.calculator.${e.toLowerCase()}`), key: e };
  });

  const { locale, dashboardOutletDimensions } = useAppSelector((state) => state.dashboard);

  const MAX_PLOTS = 10;

  const [state, setState, getState] = useStateExtended<PCalculatorIndexState>({
    isPageInitialized: false,
    showParameterDescriptionModal: false,
    optionsConfigInput: optionsConfigDefault,
    optionsConfigLoaded: optionsConfigDefault,
    optionsTable: {
      state: { isLoading: true, isError: false }, params: {}, data: undefined
    },
    optionsTableDerivatives: {
      state: { isLoading: true, isError: false }
    },
    plot2dPlots: [],
    plot3dPlots: [],
    plotSimulation: {
      state: { isLoading: true, isError: false }, params: {}, data: undefined
    },
    targetScrollProgress: 0
  });

  const fetchDerivativeOptions = async (config?: IOptionsCalculatorConfig) => {
    updateStateAttribute<PCalculatorIndexState>(
      {
        optionsTableDerivatives: {
          ...state.optionsTableDerivatives,
          state: {
            ...state.optionsTableDerivatives?.state,
            isLoading: true
          }
        }
      },
      setState
    );
    const res = keycloak.authenticated
      ? await SvCalculatorServices.authGetOptionsTableDerivatives(config)
      : await SvCalculatorServices.getOptionsTableDerivatives(config, factsheetId);

    updateStateAttribute<PCalculatorIndexState>(
      {
        optionsTableDerivatives: {
          ...state.optionsTableDerivatives,
          state: {
            isLoading: false,
            isError: res.hasError(),
            strError: res.getErrorString()
          },
          data: res.getData()
        }
      },
      setState
    );
  };

  const handleInputChange = (input: Partial<IOptionsCalculatorConfig>) => {
    const optionsConfigInput = { ...state.optionsConfigLoaded, ...input };
    updateStateAttribute<PCalculatorIndexState>({ optionsConfigInput }, setState);
  };

  const handleValidChange = async (newConfig: IOptionsCalculatorConfig, allowPageNotInitialized?: boolean) => {
    if (!allowPageNotInitialized && !state.isPageInitialized) return;

    // Set loading states for all parts of the UI
    setState((s) => ({
      ...s,
      optionsConfigInput: newConfig,
      optionsTable: { ...s.optionsTable, state: { ...s.optionsTable.state, isLoading: true } },
      plot2dPlots: (newConfig.plot2dConfigs ?? []).map(config => {
        const existingPlot = s.plot2dPlots.find(p => p.config.id === config.id);
        return {
          config, data: {
            ...(existingPlot?.data ?? { params: {}, data: undefined }), state: { isLoading: true, isError: false }
          }
        };
      }),
      plot3dPlots: (newConfig.plot3dConfigs ?? []).map(config => {
        const existingPlot = s.plot3dPlots.find(p => p.config.id === config.id);
        return {
          config, data: {
            ...(existingPlot?.data ?? { params: {}, data: undefined }), state: { isLoading: true, isError: false }
          }
        };
      }),
      plotSimulation: { ...s.plotSimulation, state: { ...s.plotSimulation.state, isLoading: true } }
    }));

    const plot2dPromises = (newConfig.plot2dConfigs ?? []).map(
      async (config) => {
        const configWithAxes = {
          ...newConfig,
          plotX: config.plotX,
          plotY: config.plotY
        };
        return keycloak.authenticated
          ? await SvCalculatorServices.authGetPlot2d(configWithAxes)
          : await SvCalculatorServices.getPlot2d(configWithAxes, factsheetId);
      });

    const plot3dPromises = (newConfig.plot3dConfigs ?? []).map(
      async (config) => {
        const configWithAxes = {
          ...newConfig,
          plot3dX: config.plot3dX,
          plot3dY: config.plot3dY,
          plot3dZ: config.plot3dZ
        };

        return keycloak.authenticated
          ? await SvCalculatorServices.authGetPlot3d(configWithAxes)
          : await SvCalculatorServices.getPlot3d(configWithAxes, factsheetId);
      });


    const tablePromise = keycloak.authenticated
      ? SvCalculatorServices.authGetOptionsTable(newConfig)
      : SvCalculatorServices.getOptionsTable(newConfig, factsheetId);


    const simulationPromise = keycloak.authenticated
      ? SvCalculatorServices.authGetPlotSimulation(newConfig)
      : SvCalculatorServices.getPlotSimulation(newConfig, factsheetId);

    // First, we call the Table endpoint
    const tableRes = await tablePromise;

    updateStateAttribute<PCalculatorIndexState>({
      optionsTable: {
        ...(await getState()).optionsTable,
        state: {
          isLoading: false,
          isError: tableRes.hasError(),
          strError: tableRes.getErrorString()
        },
        data: tableRes.getData()
      }
    }, setState);

    // Now await remaining promises
    const [simulationRes, ...plot2dResultsAnd3dResults] = await Promise.all([
      simulationPromise,
      ...plot2dPromises,
      ...plot3dPromises
    ]);

    const plot2dResults = plot2dResultsAnd3dResults.slice(0, plot2dPromises.length);
    const plot3dResults = plot2dResultsAnd3dResults.slice(plot2dPromises.length);

    updateStateAttribute<PCalculatorIndexState>({
      plot2dPlots: (newConfig.plot2dConfigs ?? []).map((config, index) => ({
        config,
        data: {
          state: {
            isLoading: false,
            isError: plot2dResults[index].hasError(),
            strError: plot2dResults[index].getErrorString()
          },
          params: {},
          data: plot2dResults[index].getData()
        }
      })),
      plot3dPlots: (newConfig.plot3dConfigs ?? []).map((config, index) => ({
        config,
        data: {
          state: {
            isLoading: false,
            isError: plot3dResults[index].hasError(),
            strError: plot3dResults[index].getErrorString()
          },
          params: {},
          data: plot3dResults[index].getData()
        }
      })),
      plotSimulation: {
        ...(await getState()).plotSimulation,
        state: { isLoading: false, isError: simulationRes.hasError(), strError: simulationRes.getErrorString() },
        data: simulationRes.getData()
      },
      optionsConfigLoaded: newConfig
    }, setState);

    localStorage.setItem(ELocalStorageItems.CALCULATOR_CONFIG, JSON.stringify(newConfig));
  };

  const handleTableChange = async (newConfig: IOptionsCalculatorConfig) => {
    // 1. Update config state and save to local storage
    setState((s) => ({
      ...s,
      optionsConfigInput: newConfig,
      optionsTable: { ...s.optionsTable, state: { ...s.optionsTable.state, isLoading: true } }
    }));
    localStorage.setItem(ELocalStorageItems.CALCULATOR_CONFIG, JSON.stringify(newConfig));

    // 2. Fetch only the table data
    const tableRes = keycloak.authenticated
      ? await SvCalculatorServices.authGetOptionsTable(newConfig):
      await SvCalculatorServices.getOptionsTable(newConfig, factsheetId);

    // 3. Update the table state with new data
    setState((s) => ({
      ...s,
      optionsTable: {
        ...s.optionsTable,
        optionsConfigLoaded: newConfig,
        state: { isLoading: false, isError: tableRes.hasError(), strError: tableRes.getErrorString() },
        data: tableRes.getData()
      }
    }));
  };

  const handle2dPlotConfigChange = async (plotId: string, newConfig: IOptionsCalculatorConfig) => {
    const plotConfig = newConfig.plot2dConfigs?.find((p) => p.id === plotId);
    if (!plotConfig) return;

    // 1. Update config state and save to local storage
    setState((s) => ({
      ...s,
      optionsConfigInput: newConfig,
      plot2dPlots: s.plot2dPlots.map((p) =>
        p.config.id === plotId
          ? {
            ...p,
            data: { ...p.data, state: { ...p.data.state, isLoading: true } }
          }
          : p
      )
    }));
    localStorage.setItem(ELocalStorageItems.CALCULATOR_CONFIG, JSON.stringify(newConfig));

    // 2. Fetch only the updated 2D plot's data
    const configWithAxes = {
      ...newConfig,
      plotX: plotConfig.plotX,
      plotY: plotConfig.plotY
    };

    const res = keycloak.authenticated
      ? await SvCalculatorServices.authGetPlot2d(configWithAxes)
      : await SvCalculatorServices.getPlot2d(configWithAxes, factsheetId);

    // 3. Update the specific 2D plot's state with new data
    setState((s) => ({
      ...s,
      optionsConfigLoaded: newConfig,
      plot2dPlots: s.plot2dPlots.map((p) =>
        p.config.id === plotId
          ? {
            config: plotConfig,
            data: {
              ...p.data,
              state: { isLoading: false, isError: res.hasError(), strError: res.getErrorString() },
              data: res.getData()
            }
          }
          : p
      )
    }));
  };

  const add2dPlot = async () => {
    const currentConfigs = state.optionsConfigInput.plot2dConfigs ?? [];
    if (currentConfigs.length >= MAX_PLOTS) return;

    const newPlotConfig: I2DPlotConfig = {
      id: `plot2d-${Date.now()}`, plotX: EOptionsCalculatorParameters.S, plotY: ['price']
    };

    const newFullConfig = {
      ...state.optionsConfigInput, plot2dConfigs: [...currentConfigs, newPlotConfig]
    };

    // 1. Add the new plot to the state in a loading state
    setState(s => ({
      ...s, optionsConfigInput: newFullConfig, plot2dPlots: [...s.plot2dPlots, {
        config: newPlotConfig, data: {
          state: { isLoading: true, isError: false }, params: {}, data: undefined
        }
      }]
    }));
    localStorage.setItem(ELocalStorageItems.CALCULATOR_CONFIG, JSON.stringify(newFullConfig));

    // 2. Asynchronously fetch the data for the new plot
    const configWithAxes = {
      ...newFullConfig,
      plotX: newPlotConfig.plotX,
      plotY: newPlotConfig.plotY
    };

    const res = keycloak.authenticated
      ? await SvCalculatorServices.authGetPlot2d(configWithAxes)
      : await SvCalculatorServices.getPlot2d(configWithAxes, factsheetId);

    // 3. Update the state of the new plot with the fetched data
    setState(s => ({
      ...s, optionsConfigLoaded: newFullConfig, plot2dPlots: s.plot2dPlots.map(p => p.config.id === newPlotConfig.id ? {
        ...p, data: {
          ...p.data,
          state: { isLoading: false, isError: res.hasError(), strError: res.getErrorString() },
          data: res.getData()
        }
      } : p)
    }));
  };

  const delete2dPlot = (plotId: string) => {
    const currentConfigs = state.optionsConfigInput.plot2dConfigs ?? [];
    if (currentConfigs.length <= 1) return;

    const newFullConfig = {
      ...state.optionsConfigInput, plot2dConfigs: currentConfigs.filter(p => p.id !== plotId)
    };

    // Just update the state. No network call.
    setState(s => ({
      ...s,
      optionsConfigInput: newFullConfig,
      optionsConfigLoaded: newFullConfig,
      plot2dPlots: s.plot2dPlots.filter(p => p.config.id !== plotId)
    }));

    localStorage.setItem(ELocalStorageItems.CALCULATOR_CONFIG, JSON.stringify(newFullConfig));
  };

  const update2dPlotConfig = async (plotId: string, configUpdate: Partial<I2DPlotConfig>) => {
    const newFullConfig = {
      ...state.optionsConfigInput,
      plot2dConfigs: (state.optionsConfigInput.plot2dConfigs ?? []).map(p => p.id === plotId ? { ...p, ...configUpdate } : p)
    };
    await handle2dPlotConfigChange(plotId, newFullConfig);
  };

  const handle3dPlotConfigChange = async (plotId: string, newConfig: IOptionsCalculatorConfig) => {
    const plotConfig = newConfig.plot3dConfigs?.find((p) => p.id === plotId);
    if (!plotConfig) return;

    // 1. Update config state and save to local storage
    setState((s) => ({
      ...s,
      optionsConfigInput: newConfig,
      plot3dPlots: s.plot3dPlots.map((p) =>
        p.config.id === plotId
          ? {
            ...p,
            data: { ...p.data, state: { ...p.data.state, isLoading: true } }
          }
          : p
      )
    }));
    localStorage.setItem(ELocalStorageItems.CALCULATOR_CONFIG, JSON.stringify(newConfig));

    // 2. Fetch only the updated 2D plot's data
    const configWithAxes = {
      ...newConfig,
      plot3dX: plotConfig.plot3dX,
      plot3dY: plotConfig.plot3dY,
      plot3dZ: plotConfig.plot3dZ,
    };

    const res = keycloak.authenticated
      ? await SvCalculatorServices.authGetPlot3d(configWithAxes)
      : await SvCalculatorServices.getPlot3d(configWithAxes, factsheetId);

    // 3. Update the specific 2D plot's state with new data
    setState((s) => ({
      ...s,
      optionsConfigLoaded: newConfig,
      plot3dPlots: s.plot3dPlots.map((p) =>
        p.config.id === plotId
          ? {
            config: plotConfig,
            data: {
              ...p.data,
              state: { isLoading: false, isError: res.hasError(), strError: res.getErrorString() },
              data: res.getData()
            }
          }
          : p
      )
    }));
  };

  const add3dPlot = async () => {
    const currentConfigs = state.optionsConfigInput.plot3dConfigs ?? [];
    if (currentConfigs.length >= MAX_PLOTS) return;

    const newPlotConfig: I3DPlotConfig = {
      id: `plot3d-${Date.now()}`,
      plot3dX: EOptionsCalculatorParameters.S,
      plot3dY: EOptionsCalculatorParameters.v,
      plot3dZ: 'Delta'
    };

    const newFullConfig = {
      ...state.optionsConfigInput, plot3dConfigs: [...currentConfigs, newPlotConfig]
    };

    // 1. Add the new plot to the state in a loading state
    setState(s => ({
      ...s, optionsConfigInput: newFullConfig, plot3dPlots: [...s.plot3dPlots, {
        config: newPlotConfig, data: {
          state: { isLoading: true, isError: false }, params: {}, data: undefined
        }
      }]
    }));
    localStorage.setItem(ELocalStorageItems.CALCULATOR_CONFIG, JSON.stringify(newFullConfig));

    // 2. Asynchronously fetch the data for the new plot
    const configWithAxes = {
      ...newFullConfig,
      plot3dX: newPlotConfig.plot3dX,
      plot3dY: newPlotConfig.plot3dY,
      plot3dZ:newPlotConfig.plot3dZ,
    };

    const res = keycloak.authenticated
      ? await SvCalculatorServices.authGetPlot3d(configWithAxes)
      : await SvCalculatorServices.getPlot3d(configWithAxes, factsheetId);

    // 3. Update the state of the new plot with the fetched data
    setState(s => ({
      ...s, optionsConfigLoaded: newFullConfig, plot3dPlots: s.plot3dPlots.map(p => p.config.id === newPlotConfig.id ? {
        ...p, data: {
          ...p.data,
          state: { isLoading: false, isError: res.hasError(), strError: res.getErrorString() },
          data: res.getData()
        }
      } : p)
    }));
  };

  const delete3dPlot = (plotId: string) => {
    const currentConfigs = state.optionsConfigInput.plot3dConfigs ?? [];
    if (currentConfigs.length <= 1) return;

    const newFullConfig = {
      ...state.optionsConfigInput, plot3dConfigs: currentConfigs.filter(p => p.id !== plotId)
    };

    // Just update the state. No network call.
    setState(s => ({
      ...s,
      optionsConfigInput: newFullConfig,
      optionsConfigLoaded: newFullConfig,
      plot3dPlots: s.plot3dPlots.filter(p => p.config.id !== plotId)
    }));

    localStorage.setItem(ELocalStorageItems.CALCULATOR_CONFIG, JSON.stringify(newFullConfig));
  };

  const update3dPlotConfig = async (plotId: string, configUpdate: Partial<I3DPlotConfig>) => {
    const newFullConfig = {
      ...state.optionsConfigInput,
      plot3dConfigs: (state.optionsConfigInput.plot3dConfigs ?? []).map(p => p.id === plotId ? { ...p, ...configUpdate } : p)
    };
    await handle3dPlotConfigChange(plotId, newFullConfig);
  };

  const cLocalizedTextStringPageTitle = CLocalizedTextString('topics.calculator.page.title');

  useEffect(() => {
    (async () => {
      const savedConfigRaw = localStorage.getItem(ELocalStorageItems.CALCULATOR_CONFIG);
      const savedConfig = savedConfigRaw ? JSON.parse(savedConfigRaw) : {};

      if (!savedConfig.plot2dConfigs || savedConfig.plot2dConfigs.length === 0) {
        savedConfig.plot2dConfigs = optionsConfigDefault.plot2dConfigs;
      }

      const initConfig = { ...optionsConfigDefault, ...savedConfig };

      await fetchDerivativeOptions(initConfig);
      await handleValidChange(initConfig, true);

      setState((s) => ({ ...s, isPageInitialized: true }));
    })();
  }, [locale]);

  const debouncedHandleValidChange = useDebouncedCallback(async (configUpdate: Partial<IOptionsCalculatorConfig>) => {
    await handleValidChange({ ...state.optionsConfigInput, ...configUpdate });
  });

  const calculatorParameterComponent = (
    <CCalculatorParameters optionsTable={state.optionsTable}
                           optionsConfigInput={state.optionsConfigInput}
                           handleInputChange={handleInputChange}
                           handleValidChange={(configUpdate) => {
                             debouncedHandleValidChange(configUpdate);
                           }}
                           enableReset={JSON.stringify(state.optionsConfigLoaded) !== JSON.stringify(optionsConfigDefault)}
                           onReset={async () => {
                             await handleValidChange(optionsConfigDefault);
                           }}
                           tableState={state.optionsTable.state} />);

  useEffect(() => {
    const targetScrollProgress = stickyHeaderRef.current?.getBoundingClientRect().top;
    if ((targetScrollProgress ?? 0) > 0) {
      updateStateAttribute<PCalculatorIndexState>({ targetScrollProgress: (targetScrollProgress ?? 0) - 16 }, setState);
    }
  }, [state.optionsConfigInput, dashboardOutletDimensions.w]);


  useEffect(() => {
    (async () => {
      await SvUsageTrackingServices.createUsageTrackingRecord(usageTrackingParams);
    })();
  }, []);


  return (<Page title={cLocalizedTextStringPageTitle} stickyHeader={calculatorParameterComponent} padStickyHeader
                targetScrollProgress={state.targetScrollProgress}>
    <CPageModule>
      <h2>
        <CLocalizedText dictKey={'topics.calculator.intro.heading'} />
      </h2>
      <div ref={stickyHeaderRef}>
        {calculatorParameterComponent}
      </div>
    </CPageModule>
    <CCalculatorTable optionsConfigDefault={optionsConfigDefault}
                      optionsTable={state.optionsTable}
                      optionsConfigInput={state.optionsConfigInput}
                      optionsTableDerivatives={state.optionsTableDerivatives}
                      onUpdateStateAttribute={async (partialState) => {
                        const newConfig = { ...state.optionsConfigInput, ...partialState.optionsConfigInput };
                        await handleTableChange(newConfig);
                      }} />
    <CPageModule fullWidth>
      <CPageModule submodule>
        <h3>
          <CLocalizedText dictKey={'topics.calculator.twoDimPlot.heading'} />
        </h3>
      </CPageModule>
      <CPageModule fullWidth submodule apiRequestState={state.optionsTableDerivatives.state}
                   showContent={!!state.optionsTableDerivatives.data}>
        <CCalculator2dPlotManager
          plots={state.plot2dPlots}
          maxPlots={MAX_PLOTS}
          parameterOptions={parameterOptions}
          optionsTableDerivatives={state.optionsTableDerivatives}
          addPlot={add2dPlot}
          deletePlot={delete2dPlot}
          updatePlotConfig={update2dPlotConfig}
          optionsConfigDefault={optionsConfigDefault}
        />
      </CPageModule>
        {state.optionsTableDerivatives.data &&
          <CPageModule fullWidth submodule apiRequestState={state.optionsTableDerivatives.state}
                       showContent={!!state.optionsTableDerivatives.data}>
            <CCalculator3dPlotManager
              plots={state.plot3dPlots}
              maxPlots={MAX_PLOTS}
              parameterOptions={parameterOptions}
              optionsConfigLoaded={state.optionsConfigLoaded}
              optionsConfigInput={state.optionsConfigInput}
              optionsTableDerivatives={state.optionsTableDerivatives}
              addPlot={add3dPlot}
              deletePlot={delete3dPlot}
              updatePlotConfig={update3dPlotConfig}
              optionsConfigDefault={optionsConfigDefault}
            />
          </CPageModule>
        }
    </CPageModule>
    <CCalculatorSimulation plotSimulation={state.plotSimulation}/>
  </Page>);
}
